import { ArrowRightIcon } from "@heroicons/react/24/outline";
import type { CSSProperties, MouseEvent } from "react";
import {
  createRabbitHoleCoverBackground,
  createRabbitHoleEditionDesign,
  resolveRabbitHoleCoverPalette,
  splitEditionTitle,
  type RabbitHoleCoverPalette,
  type RabbitHoleEditionDesign,
} from "../utils/rabbitHoleEditionDesign";

type RabbitHoleEditionCoverProps = {
  topic: string;
  title: string;
  language: string;
  languageLabel: string;
  weekStart: string;
  weekEnd: string;
  dayIndex: number;
  completed: number;
  total: number;
  dayLabel: string;
  progressLabel: string;
  verticalLabel: string;
  dateRangeLabel: string;
  isRead?: boolean;
  titlePending?: boolean;
  unavailableLabel?: string;
  actionLabel?: string;
  readActionLabel?: string;
  onAction?: () => void;
  variant?: "full" | "compact" | "mini";
  topicHeaderLines?: number;
  titleMaxLines?: number;
  designTopic?: string;
  designWeekStart?: string;
  showTopicHeader?: boolean;
  featured?: boolean;
  previewOnly?: boolean;
  personal?: boolean;
  testId?: string;
};

type RabbitHoleRecommendationCoverProps = {
  topic: string;
  language: string;
  weekStart: string;
  verticalLabel: string;
  recommendationLabel: string;
  titlePending?: boolean;
  variant?: "full" | "compact";
  featured?: boolean;
  testId?: string;
};

const COVER_LANGUAGE_TAGS: Record<string, string> = {
  ENGLISH: "en",
  FRENCH: "fr",
  GERMAN: "de",
  ITALIAN: "it",
};

function withAlpha(hex: string, alpha: number): string {
  const normalized = hex.replace("#", "");
  const red = parseInt(normalized.slice(0, 2), 16);
  const green = parseInt(normalized.slice(2, 4), 16);
  const blue = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

function LanguageSignature({
  design,
  color,
  opacityScale = 1,
}: {
  design: RabbitHoleEditionDesign;
  color?: string;
  opacityScale?: number;
}) {
  if (design.signature === "none") return null;

  const sharedStyle: CSSProperties = {
    color: color ?? design.ink,
    opacity: design.signatureOpacity * opacityScale,
  };

  if (design.signature === "corner-lines") {
    return (
      <div
        className="pointer-events-none absolute h-10 w-10 border-r border-t border-current"
        style={{
          ...sharedStyle,
          right: `${design.signatureOffset}%`,
          top: "14%",
        }}
        aria-hidden="true"
      />
    );
  }
  if (design.signature === "baseline-ticks") {
    return (
      <div
        className="pointer-events-none absolute left-[12%] right-[12%] h-2"
        style={{
          ...sharedStyle,
          bottom: `${design.signatureOffset}%`,
          backgroundImage: `repeating-linear-gradient(90deg, currentColor 0 5px, transparent 5px 12px)`,
        }}
        aria-hidden="true"
      />
    );
  }
  if (design.signature === "arc-line") {
    return (
      <div
        className="pointer-events-none absolute h-8 w-16 rounded-t-[100%] border-t border-current"
        style={{
          ...sharedStyle,
          right: `${design.signatureOffset}%`,
          top: "17%",
        }}
        aria-hidden="true"
      />
    );
  }
  return (
    <div
      className="pointer-events-none absolute left-[12%] right-[12%] border-t border-dotted border-current"
      style={{ ...sharedStyle, bottom: `${design.signatureOffset}%` }}
      aria-hidden="true"
    />
  );
}

const TITLE_SIZE_CLASSES = {
  full: {
    xl: "text-[clamp(3.15rem,13vw,4.6rem)]",
    lg: "text-[clamp(2.65rem,11vw,4rem)]",
    md: "text-[clamp(2.2rem,9vw,3.35rem)]",
    sm: "text-[clamp(1.8rem,7.4vw,2.75rem)]",
  },
  compact: {
    xl: "text-[clamp(2.1rem,8vw,3rem)]",
    lg: "text-[clamp(1.8rem,7vw,2.55rem)]",
    md: "text-[clamp(1.55rem,6vw,2.15rem)]",
    sm: "text-[clamp(1.3rem,5vw,1.8rem)]",
  },
  mini: {
    xl: "text-[1.35rem]",
    lg: "text-[1.2rem]",
    md: "text-[1.05rem]",
    sm: "text-[0.92rem]",
  },
} as const;

const TITLE_SIZE_EXPRESSIONS = {
  full: {
    xl: "clamp(3.15rem, 13vw, 4.6rem)",
    lg: "clamp(2.65rem, 11vw, 4rem)",
    md: "clamp(2.2rem, 9vw, 3.35rem)",
    sm: "clamp(1.8rem, 7.4vw, 2.75rem)",
  },
  compact: {
    xl: "clamp(2.1rem, 8vw, 3rem)",
    lg: "clamp(1.8rem, 7vw, 2.55rem)",
    md: "clamp(1.55rem, 6vw, 2.15rem)",
    sm: "clamp(1.3rem, 5vw, 1.8rem)",
  },
  mini: {
    xl: "1.35rem",
    lg: "1.2rem",
    md: "1.05rem",
    sm: "0.92rem",
  },
} as const;

const COMPACT_COVER_SIZE_CLASSES = "aspect-[0.74] max-h-[34rem] min-h-[340px]";

const PERSONAL_COVER_PALETTE: RabbitHoleCoverPalette = {
  paper: "#ebe9e4",
  ink: "#131518",
  primaryAccent: "#505a94",
  secondaryAccent: "#9299c3",
  spineTone: "#cec9c0",
};

const PERSONAL_COVER_BACKGROUND = [
  "repeating-linear-gradient(94deg, transparent 0 6px, rgba(19,21,24,.03) 7px 8px)",
  "radial-gradient(78% 64% at 72% 84%, rgba(80,90,148,.34) 0%, rgba(146,153,195,.22) 46%, transparent 78%)",
  "linear-gradient(168deg, #ebe9e4 0%, #ebe9e4 54%, rgba(146,153,195,.26) 100%)",
].join(", ");

export function RabbitHoleCompactEditionHeader({
  languageLabel,
  titleLayout,
  headerLines,
  testId,
}: {
  languageLabel: string;
  titleLayout: ReturnType<typeof splitEditionTitle>;
  headerLines: number;
  testId: string;
}) {
  const safeHeaderLines = Math.max(1, Math.min(4, headerLines)) as
    | 1
    | 2
    | 3
    | 4;
  const mobileTitle = titleLayout.lines.join(" ");

  return (
    <header
      className="mb-4 w-full min-w-0 pr-10 lg:mb-2 lg:h-[3.5rem] lg:pr-0"
      data-testid={`${testId}-header`}
      data-topic-header-lines={safeHeaderLines}
    >
      <p
        className="text-[0.7rem] font-medium tracking-[0.14em] text-slate-500"
        data-testid={`${testId}-space-label`}
      >
        {languageLabel}
      </p>
      <h2
        className="mt-1 w-full min-w-0 font-serif text-[1rem] leading-[1.08] text-slate-950 [hyphens:auto] [overflow-wrap:anywhere] sm:text-[1.05rem] lg:line-clamp-2 lg:text-[1.15rem] lg:leading-[0.98]"
        data-testid={`${testId}-topic`}
        title={mobileTitle}
      >
        {mobileTitle}
      </h2>
    </header>
  );
}

function getFittedTitleFontSize(
  variant: NonNullable<RabbitHoleEditionCoverProps["variant"]>,
  layout: ReturnType<typeof splitEditionTitle>,
): string {
  const safeWidth = Math.max(layout.longestLineWeight, 1);
  const safeLineCount = Math.max(layout.lines.length, 1);
  const widthBound = (100 / (safeWidth * 0.56)).toFixed(3);
  const heightBound = (100 / (safeLineCount * 1.08)).toFixed(3);
  return `min(${TITLE_SIZE_EXPRESSIONS[variant][layout.size]}, ${widthBound}cqw, ${heightBound}cqh)`;
}

export function RabbitHoleEditionCover({
  topic,
  title,
  language,
  languageLabel,
  weekStart,
  dayIndex,
  completed,
  total,
  dayLabel,
  progressLabel,
  verticalLabel,
  dateRangeLabel,
  isRead = false,
  titlePending = false,
  unavailableLabel,
  actionLabel,
  readActionLabel,
  onAction,
  variant = "full",
  topicHeaderLines,
  titleMaxLines = 4,
  designTopic,
  designWeekStart,
  showTopicHeader = true,
  featured = false,
  previewOnly = false,
  personal = false,
  testId = "rabbit-hole-edition",
}: RabbitHoleEditionCoverProps) {
  const design = createRabbitHoleEditionDesign({
    language,
    topic: designTopic ?? topic,
    weekStart: designWeekStart ?? weekStart,
    dayIndex,
  });
  const isMini = variant === "mini";
  const isCompact = variant === "compact";
  const visualVariant = featured || previewOnly ? "compact" : variant;
  const topicLayout = splitEditionTitle(topic, {
    variant: isMini ? "mini" : isCompact ? "full" : "compact",
    maxLines: variant === "mini" ? 2 : 3,
    forceEditorialPair: false,
  });
  const titleLayout = splitEditionTitle(title, {
    variant: visualVariant,
    maxLines: titleMaxLines,
  });
  const fittedTitleFontSize = getFittedTitleFontSize(
    visualVariant,
    titleLayout,
  );
  const safeTopicHeaderLines = Math.max(
    1,
    Math.min(3, topicHeaderLines ?? topicLayout.lines.length),
  ) as 1 | 2 | 3;
  const tone = isRead ? "read" : "active";
  const palette = personal
    ? PERSONAL_COVER_PALETTE
    : resolveRabbitHoleCoverPalette(design, tone);
  const coverBackground = personal
    ? PERSONAL_COVER_BACKGROUND
    : createRabbitHoleCoverBackground(design, tone);
  const canOpen = Boolean(
    onAction && (isMini || actionLabel) && !titlePending && title,
  );
  const useReadAction = isRead && Boolean(readActionLabel);
  const resolvedActionLabel =
    useReadAction && readActionLabel ? readActionLabel : actionLabel;

  const handleAction = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onAction?.();
  };

  const handleMiniAction = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onAction?.();
  };
  const handleCardAction = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onAction?.();
  };

  return (
    <article
      className={`min-w-0 ${
        isMini
          ? `w-full ${canOpen ? "cursor-pointer" : ""}`
          : isCompact
            ? `w-full lg:mx-auto lg:max-w-[18rem] ${canOpen ? "cursor-pointer" : ""}`
            : `mx-auto w-full ${featured ? "max-w-[32rem]" : "max-w-[25rem] lg:max-w-[22rem]"} ${canOpen ? "cursor-pointer" : ""}`
      }`}
      onClick={
        canOpen ? (isMini ? handleMiniAction : handleCardAction) : undefined
      }
      onKeyDown={
        canOpen
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                event.stopPropagation();
                onAction?.();
              }
            }
          : undefined
      }
      role={canOpen ? "button" : undefined}
      tabIndex={canOpen ? 0 : undefined}
      data-testid={testId}
      data-edition-family={design.familyId}
      data-edition-variation={design.variationId}
      data-edition-signature={design.signature}
      data-edition-progress={`${completed}/${total}`}
      data-edition-read={isRead ? "true" : "false"}
      data-personal-edition={personal ? "true" : "false"}
    >
      {showTopicHeader && !isMini && isCompact ? (
        <RabbitHoleCompactEditionHeader
          languageLabel={languageLabel}
          titleLayout={topicLayout}
          headerLines={safeTopicHeaderLines}
          testId={testId}
        />
      ) : showTopicHeader && !isMini ? (
        <RabbitHoleCompactEditionHeader
          languageLabel={languageLabel}
          titleLayout={topicLayout}
          headerLines={topicLayout.lines.length}
          testId={testId}
        />
      ) : null}

      <div
        className={`relative isolate w-full overflow-hidden rounded-[4px] border border-black/10 ${
          isMini
            ? "aspect-[0.72] min-h-0"
            : featured
              ? "aspect-[1.45] min-h-0"
              : isCompact
                ? COMPACT_COVER_SIZE_CLASSES
                : "aspect-[0.72] max-h-[42rem] min-h-[420px]"
        }`}
        style={{
          color: palette.ink,
          backgroundColor: palette.paper,
          backgroundImage: coverBackground,
        }}
        data-testid={`${testId}-cover`}
      >
        <div
          className="absolute inset-y-0 left-0 border-r border-black/10"
          style={{
            width: `${design.spineWidth}px`,
            background: `linear-gradient(90deg, ${palette.spineTone}, ${withAlpha(palette.paper, 0.45)})`,
          }}
          aria-hidden="true"
        />
        <LanguageSignature design={design} color={palette.ink} />

        <div
          className={`${isMini ? "px-5 pb-5 pt-5" : featured ? "px-[12%] pb-[7%] pt-[8%]" : "px-[12%] pb-[10%] pt-[11%]"} relative z-10 grid h-full grid-rows-[auto_minmax(0,1fr)_auto]`}
        >
          {!previewOnly ? (
            <p
              className={`truncate font-semibold tracking-[0.08em] ${
                isMini ? "mb-3 text-[0.72rem]" : "mb-[6%] text-[0.8rem]"
              }`}
              data-testid={`${testId}-language-label`}
              title={languageLabel}
            >
              {languageLabel}
            </p>
          ) : null}
          <div
            className="min-h-0 min-w-0 overflow-hidden pr-[12%]"
            style={{ containerType: "size" }}
            data-testid={`${testId}-title-region`}
          >
            {titlePending ? (
              <div
                className={`${isMini ? "h-16" : "h-28"} w-[82%] animate-pulse rounded-sm bg-black/10`}
                data-testid={`${testId}-title-loading`}
                aria-hidden="true"
              />
            ) : title ? (
              <h3
                className={`font-serif leading-[1.02] tracking-[-0.035em] [hyphens:auto] [overflow-wrap:anywhere] ${previewOnly ? "text-[clamp(0.78rem,2.8vw,1.15rem)]" : TITLE_SIZE_CLASSES[visualVariant][titleLayout.size]}`}
                data-testid={`${testId}-title`}
                title={title}
                lang={COVER_LANGUAGE_TAGS[language.trim().toUpperCase()]}
                style={
                  previewOnly ? undefined : { fontSize: fittedTitleFontSize }
                }
              >
                {titleLayout.lines.map((line, index) => (
                  <span
                    key={`${line}-${index}`}
                    className="block whitespace-nowrap"
                  >
                    {line}
                    {index < titleLayout.lines.length - 1 ? " " : ""}
                  </span>
                ))}
              </h3>
            ) : (
              <p
                className={`${isMini ? "text-sm" : "text-lg"} max-w-[14ch] font-serif leading-tight`}
              >
                {unavailableLabel}
              </p>
            )}
          </div>

          {!isMini ? (
            <p
              className="absolute right-[5%] top-[9%] max-h-[46%] text-[0.6rem] font-medium tracking-[0.1em] opacity-70 [writing-mode:vertical-rl]"
              data-testid={`${testId}-language-signature-label`}
              title={verticalLabel}
            >
              {verticalLabel}
            </p>
          ) : null}

          {!previewOnly ? (
            <div data-testid={`${testId}-metadata`}>
              <div
                className={`min-w-0 border-b border-black/15 pb-[8%] ${
                  isMini ? "block" : "flex items-end justify-between gap-3"
                }`}
                data-testid={`${testId}-bottom-rule`}
              >
                <div className="min-w-0">
                  <p
                    className={`${
                      personal
                        ? isMini
                          ? "text-[0.75rem] font-bold"
                          : "text-[0.9rem] font-bold"
                        : isMini
                          ? "text-[0.62rem] font-medium"
                          : "text-[0.78rem] font-medium"
                    } tracking-[0.04em]`}
                  >
                    {dayLabel}
                  </p>
                  <p
                    className={`font-serif leading-tight ${
                      isMini
                        ? "mt-0.5 whitespace-nowrap text-[0.68rem]"
                        : `${isCompact ? "text-[0.78rem]" : "text-sm"} mt-1 whitespace-nowrap`
                    }`}
                    data-testid={`${testId}-progress`}
                  >
                    {progressLabel}
                  </p>
                </div>
                <p
                  className={`${isMini ? "mt-2 text-right text-[0.52rem]" : "shrink-0 text-[0.62rem]"} whitespace-nowrap tracking-[0.12em]`}
                  data-testid={`${testId}-date-range`}
                >
                  {dateRangeLabel}
                </p>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {!isMini && resolvedActionLabel ? (
        <button
          type="button"
          onClick={handleAction}
          disabled={!canOpen}
          className={`${isCompact ? "mt-3 min-h-12" : "mt-5 min-h-14"} flex w-full items-center justify-between rounded-[4px] border px-5 text-left text-sm font-medium transition ${
            useReadAction
              ? "cursor-pointer border-slate-300 bg-transparent text-slate-700 hover:bg-slate-50 disabled:cursor-default disabled:border-slate-200 disabled:bg-transparent disabled:text-slate-400"
              : "cursor-pointer border-slate-950 bg-slate-950 text-white hover:border-slate-800 hover:bg-slate-800 disabled:cursor-default disabled:border-slate-300 disabled:bg-slate-300 disabled:text-slate-500"
          }`}
          data-testid={`${testId}-action`}
          data-read-action={useReadAction ? "true" : "false"}
        >
          <span>{resolvedActionLabel}</span>
          <ArrowRightIcon className="h-5 w-5 shrink-0" />
        </button>
      ) : null}
    </article>
  );
}

export function RabbitHoleRecommendationCover({
  topic,
  language,
  weekStart,
  verticalLabel,
  recommendationLabel,
  titlePending = false,
  variant = "full",
  featured = false,
  testId = "rabbit-hole-recommendation-cover",
}: RabbitHoleRecommendationCoverProps) {
  const design = createRabbitHoleEditionDesign({
    language,
    topic,
    weekStart,
    dayIndex: 0,
  });
  const visualVariant = featured ? "compact" : variant;
  const titleLayout = splitEditionTitle(topic, {
    variant: visualVariant,
    maxLines: 4,
  });
  const fittedTitleFontSize = getFittedTitleFontSize(
    visualVariant,
    titleLayout,
  );
  const isCompact = variant === "compact";
  const palette = resolveRabbitHoleCoverPalette(design, "inactive");

  return (
    <article
      className={`relative isolate w-full overflow-hidden rounded-[4px] border border-black/10 ${
        featured
          ? "mx-auto max-w-[32rem] aspect-[1.45] min-h-0"
          : isCompact
            ? COMPACT_COVER_SIZE_CLASSES
            : "aspect-[0.72] min-h-[384px]"
      }`}
      style={{
        color: palette.ink,
        backgroundColor: palette.paper,
        backgroundImage: createRabbitHoleCoverBackground(design, "inactive"),
      }}
      data-testid={testId}
      data-edition-family={design.familyId}
      data-edition-variation={design.variationId}
      data-edition-signature={design.signature}
      data-edition-tone="inactive"
      data-edition-paper={palette.paper}
      data-edition-primary-accent={palette.primaryAccent}
    >
      <div
        className="absolute inset-y-0 left-0 border-r border-black/10 opacity-60"
        style={{
          width: `${design.spineWidth}px`,
          background: `linear-gradient(90deg, ${palette.spineTone}, ${withAlpha(palette.paper, 0.65)})`,
        }}
        aria-hidden="true"
      />
      <LanguageSignature
        design={design}
        color={palette.ink}
        opacityScale={0.62}
      />

      <div
        className={`${featured ? "px-[12%] pb-[7%] pt-[8%]" : isCompact ? "px-[11%] pb-[9%] pt-[10%]" : "px-[12%] pb-[10%] pt-[11%]"} relative z-10 grid h-full grid-rows-[auto_minmax(0,1fr)_auto]`}
      >
        <p
          className={`${isCompact ? "text-[0.72rem]" : "text-[0.8rem]"} font-semibold tracking-[0.08em]`}
          data-testid={`${testId}-vertical-label`}
        >
          {verticalLabel}
        </p>

        <div
          className="min-h-0 min-w-0 overflow-hidden pr-[12%] pt-[12%]"
          style={{ containerType: "size" }}
          data-testid={`${testId}-title-region`}
        >
          {titlePending ? (
            <div
              className={`${isCompact ? "h-20" : "h-28"} w-[82%] animate-pulse rounded-sm bg-black/10`}
              data-testid={`${testId}-title-loading`}
              aria-hidden="true"
            />
          ) : (
            <h3
              id={`${testId}-title`}
              className={`font-serif leading-[1.02] tracking-[-0.035em] [hyphens:auto] [overflow-wrap:anywhere] ${TITLE_SIZE_CLASSES[visualVariant][titleLayout.size]}`}
              data-testid={`${testId}-title`}
              title={topic}
              lang={COVER_LANGUAGE_TAGS[language.trim().toUpperCase()]}
              style={{ fontSize: fittedTitleFontSize }}
            >
              {titleLayout.lines.map((line, index) => (
                <span
                  key={`${line}-${index}`}
                  className="block whitespace-nowrap"
                >
                  {line}
                  {index < titleLayout.lines.length - 1 ? " " : ""}
                </span>
              ))}
            </h3>
          )}
        </div>

        <p
          className={`${isCompact ? "text-[0.68rem]" : "text-[0.78rem]"} border-b border-black/15 pb-[7%]`}
          data-testid={`${testId}-recommendation-label`}
        >
          {recommendationLabel}
        </p>
      </div>
    </article>
  );
}
